import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useGeri } from '@/lib/geri';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Save, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { COUNTRIES_SORTED, getPhoneFormat, getPhonePrefix } from '@/lib/countries';

export default function SupplierNew() {
  const navigate = useNavigate();
  // Geri = önceki sayfa; önceki adım yoksa eski liste adresi (bkz. lib/geri.js)
  const geri = useGeri('/erp/suppliers');
  const [searchParams] = useSearchParams();
  const { company } = useAuth();
  const returnTo = searchParams.get('return');
  const prefillName = searchParams.get('prefill_name') || '';
  const prefillTax = searchParams.get('prefill_tax') || '';
  const defaultCountry = company?.address_country || 'TR';

  const [loading, setLoading] = useState(false);
  const [savedSupplier, setSavedSupplier] = useState(null);
  const [form, setForm] = useState({
    name: prefillName, address_street: '', address_district: '', address_city: '',
    address_country: defaultCountry, email: '',
    phone_country: defaultCountry, phone: '',
    auth_phone_country: defaultCountry, authorized_phone: '',
    tax_office: '', tax_number: prefillTax
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Unvan zorunlu'); return; }
    setLoading(true);
    try {
      const res = await api.post('/suppliers', {
        name: form.name,
        address_street: form.address_street, address_district: form.address_district,
        address_city: form.address_city, address_country: form.address_country,
        email: form.email,
        phone: form.phone ? `${getPhonePrefix(form.phone_country)} ${form.phone}` : '',
        authorized_phone: form.authorized_phone ? `${getPhonePrefix(form.auth_phone_country)} ${form.authorized_phone}` : '',
        tax_office: form.tax_office, tax_number: form.tax_number
      });
      toast.success('Cari hesap olusturuldu');

      // Broadcast to other tabs
      try {
        const channel = new BroadcastChannel('erp_supplier_created');
        channel.postMessage({
          supplier_id: res.data.id,
          supplier_name: res.data.name,
          supplier_code: res.data.code
        });
        channel.close();
      } catch {}

      if (returnTo === 'invoice') {
        setSavedSupplier(res.data);
      } else {
        navigate(`/erp/suppliers/${res.data.id}`);
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Hata'); }
    setLoading(false);
  };

  if (savedSupplier) {
    return (
      <div className="max-w-md mx-auto py-12 text-center" data-testid="supplier-saved">
        <div className="w-16 h-16 rounded-full bg-[#3d6e4e]/10 flex items-center justify-center mx-auto mb-4">
          <Check size={32} className="text-[#3d6e4e]" />
        </div>
        <h2 className="text-2xl font-bold text-[#1a1916] mb-2">Cari Hesap Olusturuldu</h2>
        <p className="text-[#8a7e70] mb-1">{savedSupplier.name}</p>
        <p className="text-sm text-[#8a7e70] font-mono mb-6">{savedSupplier.code}</p>
        <p className="text-sm text-[#3d6e4e] font-medium mb-6">Fatura sayfasinda otomatik eslesti</p>
        <Button onClick={() => window.close()} className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916]">
          Bu Sekmeyi Kapat
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="supplier-new-page">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" onClick={geri} className="text-[#8a7e70] hover:text-[#1a1916]" data-testid="back-btn">
          <ArrowLeft size={18} />
        </Button>
        <h1 className="text-3xl font-bold text-[#1a1916] tracking-tight">Yeni Cari Hesap</h1>
      </div>

      <form onSubmit={handleSubmit}>
        <Card className="bg-[#faf7f2] border-[#d0c4b0] shadow-sm">
          <CardHeader className="border-b border-[#d0c4b0]">
            <CardTitle className="text-lg font-bold text-[#1a1916]">Cari Hesap Bilgileri</CardTitle>
          </CardHeader>
          <CardContent className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2 md:col-span-2">
              <Label className="text-[#3F3E39] font-medium">Unvan *</Label>
              <Input value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))}
                className="bg-white border-[#d0c4b0]" required data-testid="supplier-name" />
            </div>
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Vergi Dairesi</Label>
              <Input value={form.tax_office} onChange={(e) => setForm(p => ({ ...p, tax_office: e.target.value }))}
                className="bg-white border-[#d0c4b0]" data-testid="supplier-tax-office" />
            </div>
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Vergi No</Label>
              <Input value={form.tax_number} onChange={(e) => setForm(p => ({ ...p, tax_number: e.target.value }))}
                className="bg-white border-[#d0c4b0]" data-testid="supplier-tax-number" />
            </div>
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Telefon</Label>
              <div className="flex gap-2">
                <Select value={form.phone_country} onValueChange={(v) => setForm(p => ({ ...p, phone_country: v }))}>
                  <SelectTrigger className="w-28 bg-white border-[#d0c4b0] text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{COUNTRIES_SORTED.map(c => <SelectItem key={c.code} value={c.code}>{c.phone} {c.name}</SelectItem>)}</SelectContent>
                </Select>
                <Input value={form.phone} onChange={(e) => setForm(p => ({ ...p, phone: e.target.value }))}
                  placeholder={getPhoneFormat(form.phone_country)} className="flex-1 bg-white border-[#d0c4b0]" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">E-posta</Label>
              <Input value={form.email} onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))}
                className="bg-white border-[#d0c4b0]" />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label className="text-[#3F3E39] font-medium">Adres</Label>
              <Input value={form.address_street} onChange={(e) => setForm(p => ({ ...p, address_street: e.target.value }))}
                placeholder="Sokak / Cadde / Bina No" className="bg-white border-[#d0c4b0]" />
            </div>
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Il</Label>
              <Input value={form.address_city} onChange={(e) => setForm(p => ({ ...p, address_city: e.target.value }))}
                className="bg-white border-[#d0c4b0]" />
            </div>
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Ulke</Label>
              <Select value={form.address_country} onValueChange={(v) => setForm(p => ({ ...p, address_country: v }))}>
                <SelectTrigger className="bg-white border-[#d0c4b0]"><SelectValue /></SelectTrigger>
                <SelectContent>{COUNTRIES_SORTED.map(c => <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-3 mt-6">
          <Button type="button" variant="outline" onClick={geri} className="border-[#d0c4b0]">Iptal</Button>
          <Button type="submit" disabled={loading} className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916] font-semibold">
            <Save size={16} className="mr-2" /> {loading ? 'Kaydediliyor...' : 'Kaydet'}
          </Button>
        </div>
      </form>
    </div>
  );
}
